import { useState } from "react";
import { PhoneVerify } from "@/components/PhoneVerify";
import { extractText, wordCount } from "@/lib/extractText";

export const StageBar = ({ step }) => (
  <div className="stageBar" data-testid="studio-stage-bar">
    {["Your resume", "Target & evidence", "Optimized resume"].map((l, i) => (
      <span key={l} style={{ display: "contents" }}>
        {i > 0 && <i />}
        <span className={i + 1 < step ? "done" : i + 1 === step ? "active" : ""}><b>{i + 1}</b> {l}</span>
      </span>
    ))}
  </div>
);

const Field = ({ title, badge, kind = "", children }) => (
  <label className="builderField"><span className="fieldTitle">{title}<span className={`fieldBadge ${kind}`}>{badge}</span></span>{children}</label>
);

export const UploadStage = ({ text, setText, onNext, file, setFile }) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const onFile = async (f) => {
    if (!f) return;
    setBusy(true); setErr("");
    try { setText(await extractText(f, 8)); setFile({ name: f.name, size: f.size }); }
    catch (e) { setErr(e.message || "Could not read this file."); }
    finally { setBusy(false); }
  };
  const words = wordCount(text);
  return (
    <section className="builderCard uploadStage" data-testid="studio-step-1">
      <div className="cardIntro"><p className="stepTag">STEP 1 OF 3</p><h2>Give us your starting point.</h2>
        <p>Upload the resume you already use. We extract its text in your browser so you do not have to retype your career.</p></div>
      <div>
        <label className={`resumeDrop ${busy ? "busy" : ""}`} htmlFor="resumeUpload">
          <input id="resumeUpload" type="file" accept=".pdf,.docx,.txt" disabled={busy} data-testid="studio-file-input"
            onChange={(e) => { onFile(e.target.files?.[0]); e.currentTarget.value = ""; }} />
          <span className="uploadIcon">↑</span><b>{busy ? "Reading document…" : "Choose your resume"}</b><small>PDF, DOCX or TXT · maximum 8 MB</small>
        </label>
        {file && <div className="fileReady" data-testid="studio-file-ready"><span>✓</span><div><b>{file.name}</b><small>{(file.size / 1024 / 1024).toFixed(2)} MB · text extracted in your browser</small></div></div>}
        {err && <p className="builderError" role="alert" data-testid="studio-file-error" style={{ marginTop: 12 }}>{err}</p>}
        <div className="pasteDivider"><span>OR PASTE THE TEXT</span></div>
        <Field title="Resume content" badge="Required" kind="required">
          <textarea placeholder="Paste your existing resume here…" rows={10} value={text} onChange={(e) => { setText(e.target.value); setFile(null); }} data-testid="studio-resume-text" />
          <small>{words} words · nothing is sent until you ask us to generate</small>
        </Field>
        {words > 0 && words < 40 && <p className="builderError" data-testid="studio-short-warning">Please add the complete resume text (at least 40 words).</p>}
        <button className="builderPrimary" disabled={words < 40 || busy} onClick={onNext} data-testid="studio-continue-btn">Continue with this resume →</button>
      </div>
    </section>
  );
};

const ROLES = ["Software Engineer", "QA Automation Lead", "SDET", "Engineering Manager", "AI Application Engineer", "Generative AI Developer", "Solution Architect", "Product Manager", "Data Engineer"];

export const DetailsStage = ({ form, set, onBack, onGenerate, busy, error }) => {
  const ok = form.name.trim() && /^\S+@\S+\.\S+$/.test(form.email) && form.ph && form.target_role.trim().length >= 2 && form.consent;
  const input = (k, props = {}) => <input value={form[k]} onChange={(e) => set(k, e.target.value)} data-testid={`studio-${k.replace(/_/g, "-")}`} {...props} />;
  const area = (k, rows, ph = "") => <textarea rows={rows} placeholder={ph} value={form[k]} onChange={(e) => set(k, e.target.value)} data-testid={`studio-${k.replace(/_/g, "-")}`} />;
  return (
    <section className="builderCard detailsStage" data-testid="studio-step-2">
      <div className="cardIntro"><p className="stepTag">STEP 2 OF 3</p><h2>Tell us where this resume needs to take you.</h2>
        <p>A job title alone is not enough. Add the evidence your old resume may have missed.</p>
        <button className="textButton" onClick={onBack} data-testid="studio-back-btn">← Change resume</button></div>
      <div className="detailsForm">
        <Field title="Your name" badge="Required" kind="required">{input("name", { placeholder: "e.g. Priya Sharma", autoComplete: "name" })}</Field>
        <Field title="Email for your resume" badge="Required" kind="required">{input("email", { type: "email", placeholder: "you@example.com", autoComplete: "email" })}</Field>
        <div className="sm:col-span-2" style={{ gridColumn: "1 / -1" }}><PhoneVerify value={form.ph} onChange={(v) => set("ph", v)} testId="studio-phone" /></div>
        <Field title="Target role" badge="Required" kind="required">
          {input("target_role", { list: "roles", placeholder: "e.g. QA Automation Lead" })}
          <datalist id="roles">{ROLES.map((r) => <option key={r} value={r} />)}</datalist>
        </Field>
        <Field title="Target job description" badge="Recommended · optional" kind="recommended">{area("job_description", 5, "Paste the job description, or leave blank to optimize for the role itself.")}</Field>
        <Field title="Achievements your resume is missing" badge="Recommended · optional" kind="recommended">{area("missing_achievements", 4, "Add specific, truthful evidence the old resume missed.")}</Field>
        <Field title="Anything else to protect or emphasize?" badge="Optional">{area("emphasis", 3)}</Field>
        <div className="consentGroup">
          <label className="builderConsent">
            <input type="checkbox" checked={form.consent} onChange={(e) => set("consent", e.target.checked)} data-testid="studio-consent" />
            <span><b>Required</b> — I consent to sending my resume text and the information above to Anthropic's Claude to generate this resume, and to Art of Resume storing my name, email, source and generated resume so it can be delivered and I can request it later. It will not be added to a marketing list.</span>
          </label>
          <label className="builderConsent optionalConsent">
            <input type="checkbox" checked={form.save_local} onChange={(e) => set("save_local", e.target.checked)} data-testid="studio-save-local" />
            <span><b>Optional</b> — Remember my generated resume on this device so I can reopen it later. It stays in this browser.</span>
          </label>
        </div>
        {error && <p className="builderError" role="alert" data-testid="studio-error">{error}</p>}
        <button className="builderPrimary generateButton" disabled={!ok || busy} onClick={onGenerate} data-testid="studio-generate-btn">
          {busy ? <span className="spinner" style={{ display: "inline-block", verticalAlign: "middle", marginRight: 8 }} /> : null}Generate my optimized resume →
        </button>
        <p className="generationNote">AI can make mistakes. Verify dates, titles, metrics, links and claims.</p>
      </div>
    </section>
  );
};
